import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatCard } from "@/components/crm/StatCard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { MultiSelect } from "@/components/ui/multi-select";
import { Button } from "@/components/ui/button";
import ReportPeriodFilter from "@/components/crm/ReportPeriodFilter";
import { presetRange } from "@/lib/reportPeriods";
import { formatCurrency } from "@/lib/constants";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend, LabelList,
} from "recharts";
import {
  Building2, Users2, CalendarPlus, UserCheck, CheckCircle2, TrendingUp, IndianRupee,
  Plug, Megaphone, X,
} from "lucide-react";

// Radix Select can't use an empty-string value, so "All Branches" uses this sentinel.
const ALL_BRANCHES = "all";
const TT_STYLE = { borderRadius: 12, border: "1px solid #1B3A5C20" };
const BAR_LABEL = { position: "top", fontSize: 11, fill: "#374151", fontWeight: 600 };
const Y_DOMAIN = [0, (max) => Math.ceil((max || 0) * 1.12) || 1];

const num = (n) => Number(n || 0).toLocaleString("en-IN");
const pct = (n) => `${Number(n || 0)}%`;

// The chart only has room for a handful of bars before the labels collide; the table
// under it always carries the full list, so nothing is hidden — just not plotted.
const CHART_ROWS = 8;

// Tint a conversion cell green/red the same way the management report does, so a
// campaign that brings volume but never converts is visible without reading numbers.
const rateTint = (v) =>
  v >= 20 ? "bg-green-100 text-green-800"
  : v <= 5 ? "bg-red-100 text-red-700"
  : "text-ink";

// Option lists come back with their lead counts — show them, so picking a
// sub-source doesn't mean guessing which ones have any leads this period.
const optionList = (opts) =>
  (opts || []).map((o) => ({ value: o.value, label: `${o.value} (${num(o.leads)})` }));

export default function SourcePerformance() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [range, setRange] = useState(() => presetRange("this_month"));
  const [activePreset, setActivePreset] = useState("this_month");
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(ALL_BRANCHES);
  // Which slice of leads to judge — all four narrow the same cohort, so the KPIs,
  // charts and every tab move together.
  const [fIntegrations, setFIntegrations] = useState([]);
  const [fSources, setFSources] = useState([]);
  const [fSubSources, setFSubSources] = useState([]);

  const allowed = ["super_admin", "hq_admin", "branch_manager", "franchisee_owner"]
    .includes(user?.role);

  // Branch list, narrowed to what this role may actually pick — the backend 403s on
  // an out-of-scope branch, so the options must never offer one.
  useEffect(() => {
    if (!allowed) return;
    api.get("/branches").then((r) => {
      let list = r.data || [];
      if (user?.role === "branch_manager" && (user?.branch_ids || []).length) {
        list = list.filter((b) => (user.branch_ids || []).includes(b.id));
      } else if (user?.role === "franchisee_owner" && user?.franchise_id) {
        list = list.filter((b) => b.franchise_id === user.franchise_id);
      }
      setBranches(list);
    }).catch(() => {});
  }, [allowed, user]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { from: range.from, to: range.to };
      if (selectedBranch !== ALL_BRANCHES) params.branch_id = selectedBranch;
      if (fIntegrations.length) params.integration = fIntegrations;
      if (fSources.length) params.source = fSources;
      if (fSubSources.length) params.sub_source = fSubSources;
      // Repeated keys (source=a&source=b), NOT axios's default source[]=a — a Meta
      // form name can contain a comma, so these can't be comma-joined,
      // and FastAPI binds a List[str] param from the bare repeated key.
      const r = await api.get("/reports/source-performance", {
        params, paramsSerializer: { indexes: null },
      });
      setData(r.data);
    } catch (e) {
      setError(e.response?.status === 403
        ? "Source performance is available to admins, branch managers and franchise owners."
        : "Could not load source performance.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to, selectedBranch, fIntegrations, fSources, fSubSources]);

  useEffect(() => { if (allowed) load(); }, [allowed, load]);

  const applyPreset = (key) => {
    setActivePreset(key);
    setRange(presetRange(key));
  };
  const setCustom = (field, value) => {
    if (!value) return;
    setActivePreset(null);
    setRange((r) => ({ ...r, [field]: value }));
  };

  const options = data?.options || {};
  // Sub-source options narrow to the picked sources — a form name only means
  // something under its parent source, and the full list is unusable otherwise.
  // The server returns one entry per (source, sub-source) pair, so the same name
  // under two sources arrives twice; merge them, because MultiSelect selects by
  // value and duplicate values would render as two checkboxes that toggle as one.
  // That merge matches the server, which filters on the sub-source value alone.
  const subSourceOptions = useMemo(() => {
    const picked = new Set(fSources.map((s) => s.toLowerCase()));
    const merged = new Map();
    for (const o of options.sub_sources || []) {
      if (picked.size && !picked.has(String(o.source || "").toLowerCase())) continue;
      const key = o.value.toLowerCase();
      const cur = merged.get(key);
      if (cur) { cur.leads += o.leads; cur.sources.push(o.source); }
      else merged.set(key, { value: o.value, leads: o.leads, sources: [o.source] });
    }
    return [...merged.values()]
      .sort((a, b) => b.leads - a.leads || a.value.localeCompare(b.value));
  }, [options.sub_sources, fSources]);

  // Name the parent source when there is exactly one — form names like
  // "26/06 | Weight Loss" don't say which channel they belong to.
  const subSourceChoices = useMemo(
    () => subSourceOptions.map((o) => ({
      value: o.value,
      label: o.sources.length === 1
        ? `${o.value} — ${o.sources[0]} (${num(o.leads)})`
        : `${o.value} (${num(o.leads)})`,
    })),
    [subSourceOptions]
  );

  // Dropping a source must drop the sub-sources that hung off it, or the request
  // keeps a selection the picker no longer offers and the page returns nothing.
  useEffect(() => {
    if (!fSources.length || !fSubSources.length) return;
    const valid = new Set(subSourceOptions.map((o) => o.value.toLowerCase()));
    const kept = fSubSources.filter((v) => valid.has(v.toLowerCase()));
    if (kept.length !== fSubSources.length) setFSubSources(kept);
  }, [subSourceOptions, fSources, fSubSources]);

  const filterCount = fIntegrations.length + fSources.length + fSubSources.length;
  const clearFilters = () => {
    setFIntegrations([]); setFSources([]); setFSubSources([]);
  };

  if (!allowed) {
    return (
      <div className="rounded-xl border border-navy/10 bg-white p-8 text-center text-sm text-muted-foreground shadow-sm">
        Source performance is available to admins, branch managers and franchise owners.
      </div>
    );
  }

  const k = data?.kpis || {};
  const showRevenue = !!data?.show_revenue;

  return (
    <div className="space-y-5">
      <ReportPeriodFilter
        range={range}
        activePreset={activePreset}
        onPreset={applyPreset}
        onCustom={setCustom}
      />

      {/* Which leads to judge: branch, then the integration → source → sub-source →
          campaign chain. Every control narrows the same cohort. */}
      <div className="flex flex-wrap items-center gap-2">
        {branches.length > 0 && (
          <>
            <Building2 className="h-4 w-4 text-navy/60" />
            <Select value={selectedBranch} onValueChange={setSelectedBranch}>
              <SelectTrigger className="h-8 w-48 border-navy/20 text-sm" data-testid="source-perf-branch-select">
                <SelectValue placeholder="All Branches" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_BRANCHES}>All Branches</SelectItem>
                {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </>
        )}
        <MultiSelect
          className="h-8 w-48 text-sm"
          data-testid="source-perf-filter-integration"
          placeholder="All Integrations"
          options={optionList(options.integrations)}
          selected={fIntegrations}
          onChange={setFIntegrations}
        />
        <MultiSelect
          className="h-8 w-44 text-sm"
          data-testid="source-perf-filter-source"
          placeholder="All Sources"
          options={optionList(options.sources)}
          selected={fSources}
          onChange={setFSources}
        />
        <MultiSelect
          className="h-8 w-48 text-sm"
          data-testid="source-perf-filter-sub-source"
          placeholder="All Sub-sources"
          options={subSourceChoices}
          selected={fSubSources}
          onChange={setFSubSources}
        />
        {filterCount > 0 && (
          <Button variant="ghost" size="sm" onClick={clearFilters}
            className="h-8 text-navy/70 hover:text-navy" data-testid="source-perf-clear-filters">
            <X className="mr-1 h-3.5 w-3.5" /> Clear
          </Button>
        )}
        {loading && <span className="text-xs text-navy/50">Loading…</span>}
      </div>

      {/* Spell out the cohort — these numbers deliberately don't match the Revenue
          report, which counts by visit date instead. */}
      <p className="text-xs text-navy/60" data-testid="source-perf-scope-note">
        Leads <span className="font-medium text-navy">created</span> in the selected
        period, followed through to their own bookings, visits and revenue — whenever
        those happened. A lead created this month that visits next month still counts
        here, so these totals differ from the visit-dated Revenue report.
        {data && filterCount > 0 && (
          <> Showing <span className="font-medium text-navy">{num(k.leads)}</span> of{" "}
            {num(data.window_leads)} leads in the period.</>
        )}
      </p>

      {error ? (
        <div className="rounded-xl border border-navy/10 bg-white p-8 text-center text-sm text-muted-foreground shadow-sm">
          {error}
        </div>
      ) : !data ? (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {[...Array(8)].map((_, i) => <div key={i} className="h-28 animate-pulse rounded-xl bg-white/60" />)}
        </div>
      ) : (
        <>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard icon={Users2} label="Leads" value={num(k.leads)}
              sub={`${num(k.sub_sources)} sub-sources · ${num(k.sources)} sources`} testId="sp-kpi-leads" />
            <StatCard icon={CalendarPlus} label="Appointments Booked" value={num(k.booked)}
              sub={`${pct(k.booking_rate)} of leads booked`} testId="sp-kpi-booked" />
            <StatCard icon={UserCheck} label="Visited" value={num(k.visited)}
              sub={`${pct(k.show_rate)} show rate`} testId="sp-kpi-visited" />
            <StatCard icon={CheckCircle2} label="Converted" value={num(k.converted)}
              sub={`${num(k.lost)} lost · ${num(k.hot)} hot`} testId="sp-kpi-converted" />
            <StatCard icon={TrendingUp} label="Conversion Rate" value={pct(k.conversion_rate)}
              accent="gold" sub="converted ÷ leads created" testId="sp-kpi-conversion" />
            {showRevenue && (
              <StatCard icon={IndianRupee} label="Revenue" value={formatCurrency(k.revenue || 0)}
                accent="gold" sub="on visits from this cohort" testId="sp-kpi-revenue" />
            )}
          </div>

          <Tabs defaultValue="sub_source" className="space-y-5">
            <TabsList className="bg-white">
              <TabsTrigger value="sub_source" data-testid="sp-tab-sub-source">Sub-sources / Campaigns</TabsTrigger>
              <TabsTrigger value="source" data-testid="sp-tab-source">Lead Sources</TabsTrigger>
              <TabsTrigger value="integration" data-testid="sp-tab-integration">Integrations</TabsTrigger>
            </TabsList>

            <TabsContent value="sub_source" className="space-y-5">
              <p className="flex items-start gap-2 rounded-xl border border-navy/10 bg-navy/5 px-4 py-3 text-xs text-navy">
                <Megaphone className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                The campaign-level view: one row per sub-source within its parent
                source — the Meta lead form ("05/08 | Plain Thane Hair Loss High Intent
                | Revolo-E"), the website form, the campaign column of a synced sheet.
                Leads with no sub-source sit under "(none)".
              </p>
              <FunnelChart title="Leads → Booked → Visited by Sub-source" rows={data.by_sub_source} />
              <PerformanceTable
                title="Sub-source / Campaign Performance"
                firstHeader="Sub-source"
                rows={data.by_sub_source}
                truncated={data.by_sub_source_truncated}
                showRevenue={showRevenue}
                extraCols={["source"]}
                testId="sp-table-sub-source"
                empty="No leads created in this period"
              />
            </TabsContent>

            <TabsContent value="source" className="space-y-5">
              <FunnelChart title="Leads → Booked → Visited by Source" rows={data.by_source} />
              <PerformanceTable
                title="Lead Source Performance"
                firstHeader="Source"
                rows={data.by_source}
                showRevenue={showRevenue}
                testId="sp-table-source"
                empty="No leads created in this period"
              />
            </TabsContent>

            <TabsContent value="integration" className="space-y-5">
              <p className="flex items-start gap-2 rounded-xl border border-navy/10 bg-navy/5 px-4 py-3 text-xs text-navy">
                <Plug className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                Which pipe delivered the lead — Meta Lead Ads, the website form, the
                chatbot, JustDial, a sheet sync — as opposed to the source it was
                tagged with. The same "Facebook" source can arrive through several,
                and anything typed into the CRM by a person shows as Manual Entry.
              </p>
              <FunnelChart title="Leads → Booked → Visited by Integration" rows={data.by_integration} />
              <PerformanceTable
                title="Integration Performance"
                firstHeader="Integration"
                rows={data.by_integration}
                showRevenue={showRevenue}
                testId="sp-table-integration"
                empty="No leads created in this period"
              />
            </TabsContent>

          </Tabs>
        </>
      )}
    </div>
  );
}

const CHART_SHELL = "rounded-xl border border-navy/10 bg-white p-6 shadow-sm";

const Card = ({ title, subtitle, children }) => (
  <div className={CHART_SHELL}>
    <h3 className="font-display text-lg font-semibold text-navy">{title}</h3>
    <p className="mb-4 text-xs text-muted-foreground">{subtitle || " "}</p>
    {children}
  </div>
);

// Grouped bars: the three funnel steps side by side, so a channel that brings volume
// but never converts it is obvious at a glance.
function FunnelChart({ title, rows }) {
  const data = useMemo(
    () => (rows || []).slice(0, CHART_ROWS).map((r) => ({
      name: r.name,
      leads: r.leads,
      booked: r.booked,
      visited: r.visited,
    })),
    [rows]
  );
  const more = (rows || []).length - data.length;
  return (
    <Card
      title={title}
      subtitle={more > 0
        ? `Top ${CHART_ROWS} by lead volume — ${more} more in the table below`
        : "Leads created in the period, and what they turned into"}
    >
      {data.length === 0 ? (
        <p className="py-10 text-center text-sm text-muted-foreground">No leads in this period</p>
      ) : (
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={data} margin={{ left: 4, right: 8, top: 12 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5ddd2" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#6B7280" }} interval={0}
              angle={-15} textAnchor="end" height={64} />
            <YAxis tick={{ fontSize: 11, fill: "#6B7280" }} allowDecimals={false} domain={Y_DOMAIN} />
            <Tooltip contentStyle={TT_STYLE} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="leads" name="Leads" fill="#1B3A5C" radius={[6, 6, 0, 0]}>
              <LabelList dataKey="leads" {...BAR_LABEL} />
            </Bar>
            <Bar dataKey="booked" name="Booked" fill="#5B7B9A" radius={[6, 6, 0, 0]}>
              <LabelList dataKey="booked" {...BAR_LABEL} />
            </Bar>
            <Bar dataKey="visited" name="Visited" fill="#C49A6C" radius={[6, 6, 0, 0]}>
              <LabelList dataKey="visited" {...BAR_LABEL} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
}

// Context columns a grid can carry beside its own name. Only ever fields that are
// part of that grid's key — a column filled from whichever lead happened to create
// the row would read as a fact about the whole row without being one.
const EXTRA_COL_LABELS = { integration: "Integration", source: "Source" };

function PerformanceTable({
  title, firstHeader, rows, showRevenue, extraCols = [], truncated = 0, testId, empty,
}) {
  const list = rows || [];
  const th = "px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground";
  const td = "px-3 py-2.5 text-sm text-ink";
  const colCount = 11 + extraCols.length + (showRevenue ? 2 : 0);
  return (
    <Card
      title={title}
      subtitle={truncated > 0
        ? `Top ${list.length} by lead volume — ${truncated} smaller row(s) not shown`
        : "Sorted by lead volume"}
    >
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid={testId}>
          <thead>
            <tr className="border-b bg-cream/50 text-left">
              <th className={th}>{firstHeader}</th>
              {extraCols.map((c) => <th key={c} className={th}>{EXTRA_COL_LABELS[c]}</th>)}
              <th className={`${th} text-right`}>Leads</th>
              <th className={`${th} text-right`}>Share</th>
              <th className={`${th} text-right`}>Hot</th>
              <th className={`${th} text-right`}>Booked</th>
              <th className={`${th} text-right`}>Book %</th>
              <th className={`${th} text-right`}>Visited</th>
              <th className={`${th} text-right`}>Show %</th>
              <th className={`${th} text-right`}>Converted</th>
              <th className={`${th} text-right`}>Conv %</th>
              <th className={`${th} text-right`}>Lost</th>
              {showRevenue && <th className={`${th} text-right`}>Revenue</th>}
              {showRevenue && <th className={`${th} text-right`}>₹ / Lead</th>}
            </tr>
          </thead>
          <tbody>
            {list.length === 0 ? (
              <tr>
                <td colSpan={colCount} className="px-3 py-10 text-center text-muted-foreground">{empty}</td>
              </tr>
            ) : list.map((r, i) => (
              <tr key={`${r.integration || ""}-${r.source || ""}-${r.name}-${i}`}
                className="border-b last:border-0 hover:bg-nude/30">
                <td className={`${td} font-semibold`}>{r.name}</td>
                {extraCols.map((c) => (
                  <td key={c} className={`${td} text-muted-foreground`}>{r[c] || "—"}</td>
                ))}
                <td className={`${td} text-right font-semibold`}>{num(r.leads)}</td>
                <td className={`${td} text-right text-muted-foreground`}>{pct(r.lead_share)}</td>
                <td className={`${td} text-right text-muted-foreground`}>{num(r.hot)}</td>
                <td className={`${td} text-right`}>{num(r.booked)}</td>
                <td className={`${td} text-right text-muted-foreground`}>{pct(r.booking_rate)}</td>
                <td className={`${td} text-right`}>{num(r.visited)}</td>
                <td className={`${td} text-right text-muted-foreground`}>{pct(r.show_rate)}</td>
                <td className={`${td} text-right`}>{num(r.converted)}</td>
                <td className={`${td} text-right`}>
                  <span className={`rounded px-1.5 py-0.5 font-semibold ${rateTint(r.conversion_rate)}`}>
                    {pct(r.conversion_rate)}
                  </span>
                </td>
                <td className={`${td} text-right text-muted-foreground`}>{num(r.lost)}</td>
                {showRevenue && <td className={`${td} text-right`}>{formatCurrency(r.revenue || 0)}</td>}
                {showRevenue && <td className={`${td} text-right text-muted-foreground`}>{formatCurrency(r.revenue_per_lead || 0)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
